import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  capabilities,
  company,
  industries,
  stats,
} from "../data/companyData";
import { productPath, productsData } from "../data/productsData";
import HeroCanvas from "../components/HeroCanvas";
import HeroSlideshow from "../components/HeroSlideshow";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import Marquee from "../components/Marquee";
import Parallax from "../components/Parallax";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { pageMeta } from "../data/seoData";
import "./Home.css";

const HERO_TICKER = [
  "Direct import",
  "Certificate of analysis",
  "25 & 50 kg packing",
  "Pan-India dispatch",
  "GST registered",
];

const PILLARS = [
  {
    kicker: "Import & distribution",
    title: "Eleven compounds, continuously in stock",
    body: "Carbonates, molybdates, oxides, ammonium salts and agricultural humates, held in Mumbai and dispatched in 25 kg and 50 kg packing, part-load or bulk.",
    image: "/images/bags.webp",
    to: "/products",
    cta: "Browse the catalogue",
  },
  {
    kicker: "Custom procurement",
    title: "Sourced to your specification",
    body: "Outside the catalogue we run the search: identify the manufacturer, verify the grade, negotiate the landed cost and handle import documentation end to end.",
    image: "/images/truck.jpeg",
    to: "/contact",
    cta: "Start a sourcing brief",
  },
];

const ASSURANCES = [
  { label: "Certificate of analysis", value: "Every consignment, batch-traceable" },
  { label: "Packing", value: "25 kg / 50 kg HDPE, bulk on request" },
  { label: "Dispatch", value: "Road · Sea · Air · Cargo" },
  { label: "Payment", value: "RTGS, NEFT, cheque, DD, card" },
  { label: "Lead time", value: "Ex-stock same week, import 4-6 weeks" },
];

const Home = () => {
  const navigate = useNavigate();
  const featured = productsData.slice(0, 6);

  const openProduct = (product) => navigate(productPath(product));

  return (
    <>
      <Seo {...pageMeta.home} />

      {/* ================================================== Hero ========== */}
      <section className="hero grain">
        <HeroSlideshow />
        <HeroCanvas />
        <div className="hero__grid" />

        <div className="shell hero__inner">
          <Reveal variant="mask" as="p" className="eyebrow eyebrow--dark hero__eyebrow">
            {company.address.city}, India · Est. {company.established}
          </Reveal>

          <h1 className="hero__title">
            <Reveal variant="mask" as="span" className="hero__line" delay={0.08}>
              We source chemicals
            </Reveal>
            <Reveal variant="mask" as="span" className="hero__line" delay={0.18}>
              the <em className="editorial">industry</em>
            </Reveal>
            <Reveal variant="mask" as="span" className="hero__line" delay={0.28}>
              runs on.
            </Reveal>
          </h1>

          <Reveal as="p" className="hero__lead" delay={0.45}>
            Devanshi Corporation imports industrial chemicals, mineral salts and
            agricultural humates into India. Specified at origin, verified on
            arrival, delivered on a date we commit to.
          </Reveal>

          <Reveal className="hero__actions" delay={0.55}>
            <Link to="/products" className="btn btn--accent btn--lg">
              Explore the catalogue
              <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
            <Link to="/contact" className="btn btn--ghost-light btn--lg">
              Talk to our team
            </Link>
          </Reveal>
        </div>

        <div className="hero__foot">
          <div className="shell hero__foot-inner">
            <span className="hero__scroll mono">
              <span className="hero__scroll-rule" aria-hidden="true" />
              Scroll
            </span>
            <ul className="hero__ticker">
              {HERO_TICKER.map((item) => (
                <li key={item} className="mono">{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ================================================= Stats ========== */}
      <section className="on-dark section--tight" aria-label="Company at a glance">
        <div className="shell">
          <Reveal className="stats">
            {stats.map((s) => (
              <div className="stat" key={s.label}>
                <div className="stat__value">
                  <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} />
                </div>
                <div className="stat__label">{s.label}</div>
                <div className="stat__detail">{s.detail}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* =============================================== Pillars ========== */}
      <section className="section on-dark">
        <div className="shell">
          <div className="sec-head">
            <div>
              <Reveal as="p" className="eyebrow eyebrow--dark">Two sides to the business</Reveal>
              <Reveal as="h2" className="sec-head__title" delay={0.05}>
                Stock you can draw on, and sourcing you can brief.
              </Reveal>
            </div>
            <Reveal as="p" className="lead sec-head__aside" delay={0.1}>
              Most orders ship from what we already hold. The rest we go and
              find, with the same purity, documentation and landed-cost
              discipline.
            </Reveal>
          </div>

          <div className="pillars">
            {PILLARS.map((p, i) => (
              <Reveal className="pillar" key={p.title} delay={i * 0.1}>
                <Link to={p.to} className="pillar__link">
                  <div className="pillar__media">
                    <img src={p.image} alt="" loading="lazy" />
                  </div>
                  <p className="pillar__kicker">{p.kicker}</p>
                  <h3 className="pillar__title">{p.title}</h3>
                  <p className="pillar__body">{p.body}</p>
                  <span className="link">
                    {p.cta}
                    <span className="btn__arrow" aria-hidden="true">→</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* =============================================== Marquee ========== */}
      <section className="on-dark industries" aria-label="Industries served">
        <div className="shell">
          <p className="eyebrow eyebrow--dark industries__label">
            Serving
          </p>
        </div>
        <Marquee items={industries} duration={46} />
        <Marquee items={[...industries].reverse()} duration={54} reverse />
      </section>

      {/* ============================================= Catalogue ========== */}
      <section className="section on-light">
        <div className="shell">
          <div className="sec-head">
            <div>
              <Reveal as="p" className="eyebrow">Catalogue</Reveal>
              <Reveal as="h2" className="sec-head__title" delay={0.05}>
                In stock now
              </Reveal>
            </div>
            <Reveal className="sec-head__aside" delay={0.1}>
              <p className="lead">
                Technical and industrial grades with published purity and
                packing. Every line ships with its certificate of analysis.
              </p>
              <Link to="/products" className="link home__all">
                All {productsData.length} products
                <span className="btn__arrow" aria-hidden="true">→</span>
              </Link>
            </Reveal>
          </div>

          <Reveal className="product-grid">
            {featured.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onOpen={openProduct}
              />
            ))}
          </Reveal>
        </div>
      </section>

      {/* ========================================== Statement band ======== */}
      <Parallax src="/images/drums.jpg" alt="" className="statement">
        <div className="statement__wash" />
        <div className="shell statement__inner">
          <Reveal as="p" className="statement__quote" variant="mask">
            Twenty-five years of the same discipline: know the origin, prove the
            purity, hit the <em className="editorial">date</em>.
          </Reveal>
          <Reveal as="p" className="statement__by" delay={0.2}>
            {company.ceo} · Proprietor
          </Reveal>
        </div>
      </Parallax>

      {/* ========================================== Capabilities ========== */}
      <section className="section on-light">
        <div className="shell split">
          <div className="split__sticky">
            <Reveal as="p" className="eyebrow">How we work</Reveal>
            <Reveal as="h2" className="sec-head__title" delay={0.05}>
              Four things we do not compromise on.
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/about" className="link home__all">
                More about us
                <span className="btn__arrow" aria-hidden="true">→</span>
              </Link>
            </Reveal>
          </div>

          <div className="rows">
            {capabilities.map((c, i) => (
              <Reveal className="row" key={c.index} delay={i * 0.06}>
                <div className="row__index">{c.index}</div>
                <h3 className="row__title">{c.title}</h3>
                <p className="row__body">{c.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ Assurances ========== */}
      <section className="section--tight on-sand-1 assur">
        <div className="shell">
          <Reveal as="p" className="eyebrow">Terms at a glance</Reveal>
          <dl className="assur__grid">
            {ASSURANCES.map((a, i) => (
              <Reveal className="assur__item" key={a.label} delay={i * 0.05}>
                <dt>{a.label}</dt>
                <dd>{a.value}</dd>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      {/* =================================================== CTA ========== */}
      <section className="cta-band">
        <div className="cta-band__glow" />
        <div className="shell cta-band__inner">
          <div>
            <Reveal as="p" className="eyebrow eyebrow--dark">Next step</Reveal>
            <Reveal as="h2" className="cta-band__title" delay={0.05}>
              Send us the specification. We will quote it.
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="cta-band__actions">
              <Link to="/contact" className="btn btn--accent btn--lg">
                Request a quote
                <span className="btn__arrow" aria-hidden="true">→</span>
              </Link>
              <a
                href={`tel:${company.phones.office.tel}`}
                className="btn btn--ghost-light btn--lg">
                {company.phones.office.display}
              </a>
            </div>
            <p className="cta-band__note">
              Replies within one working day · {company.hours[0].days},{" "}
              {company.hours[0].time}
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Home;
